import { defineStore } from "pinia"
import router from "~/router"

import { DOMAIN, getSubdomain } from "~/logic"
import {
    usePersonnelStore,
    useEntrepriseStore,
    useClientStore,
    useProjetStore,
    useRdvStore,
    useDevisStore,
    useInterventionStore,
    useInterventionPlanningStore,
    useAdresseStore,
    useAppStore,
} from "~/stores"
import { Api } from "~/api/Api"
import { PersonnelResource, ClientResource } from "~/api"
import { Logger } from "~/helpers/logger"

export const useAuthStore = defineStore("auth", {
    state: () => ({
        mainToken: {
            token: "",
            tokenable_type: "",
            tokenable_id: "",
            company_id: 0,
        },
        tempToken: {
            token: "",
            tokenable_type: "",
            tokenable_id: "",
            company_id: 0,
        },
        impersonateReturnCompanyId: null as number | null,
    }),
    getters: {
        clientStore() {
            return useClientStore()
        },
        personnelStore() {
            return usePersonnelStore()
        },
        entrepriseStore() {
            return useEntrepriseStore()
        },
        getUser(): PersonnelResource | ClientResource | null {
            if (
                !this.mainToken.tokenable_type ||
                !this.mainToken.tokenable_id
            ) {
                return null
            }
            switch (this.mainToken.tokenable_type) {
                case DOMAIN.manager.model:
                    return this.personnelStore.itemById[
                        this.mainToken.tokenable_id
                    ]

                case DOMAIN.space.model:
                    return this.clientStore.itemById[
                        this.mainToken.tokenable_id
                    ]

                default:
                    return null
            }
        },
        getTokenableId() {
            return this.mainToken.tokenable_id
        },
        isPersonnel() {
            return this.mainToken.tokenable_type === DOMAIN.manager.model
        },
        isClient() {
            return this.mainToken.tokenable_type === DOMAIN.space.model
        },
        isAdmin() {
            return this.mainToken.tokenable_type === DOMAIN.admin.model
        },
        isCommercialDirector() {
            return this.getUser?.ROLES?.includes("commercial_director")
        },
        isTechnician() {
            return this.getUser?.ROLES?.includes("technician")
        },
        isCommercial() {
            return this.getUser?.ROLES?.includes("commercial")
        },
        isMetreur() {
            return this.getUser?.ROLES?.includes("metreur")
        },
        isTechnicalDirector() {
            return this.getUser?.ROLES?.includes("technical_director")
        },
        companyKey() {
            return (
                this.mainToken?.company_id ||
                this.entrepriseStore.currentItemId ||
                getSubdomain
            )
        },
        getToken() {
            return this.mainToken.token
        },
        isTokenSet() {
            return !!this.mainToken.token
        },
        isImpersonate() {
            return !!this.tempToken.token
        },
    },

    actions: {
        /**
         * Méthode privée pour attendre que tous les stores CRUD soient prêts
         * après un changement d'entreprise
         */
        async _waitForStoresReady() {
            Logger.info("auth.store", "Waiting for all stores to be ready...")

            const stores = [
                useProjetStore(),
                useClientStore(),
                useRdvStore(),
                useDevisStore(),
                useInterventionStore(),
                useInterventionPlanningStore(),
                usePersonnelStore(),
                useAdresseStore(),
            ]

            await Promise.all(stores.map((store) => store.ready))
            Logger.info("auth.store", "All stores are ready!")
        },

        async adminLogin(payload) {
            this.mainToken = await Api.admin.createAdminToken(payload)
        },
        async personnelLogin(credentials) {
            this.mainToken =
                await Api.auth.authCreatePersonnelToken(credentials)
        },

        async loginPublic(payload) {
            this.mainToken = await Api.space.spaceVerifyOtpAndCreateToken(
                payload.idClient,
                payload?.portable,
                payload?.code,
            )
        },
        async logout() {
            try {
                if (this.isImpersonate) {
                    await this.leaveImpersonate()
                } else {
                    localStorage.clear()
                    await Api.auth.logout()
                    this.$reset()
                    await router.push("/")
                }
            } catch (error) {
                throw new Error(error)
            }
        },
        async impersonate(personnel: PersonnelResource) {
            const appStore = useAppStore()
            const entrepriseStore = useEntrepriseStore()
            const oldToken = this.mainToken

            try {
                Logger.info(
                    "auth.store",
                    `Starting impersonation of personnel ${personnel.IDPERSONNEL}`,
                    {
                        fromCompany: this.mainToken.company_id,
                        toCompany: personnel.IDCOMPANY,
                    },
                )

                // Afficher le loader
                appStore.isChangingCompany = true
                appStore.changingCompanyMessage = `Connexion en tant que ${personnel.NOM_COMPLET}...`

                // Sauvegarder le token actuel et l'entreprise consultée
                this.tempToken = this.mainToken
                this.impersonateReturnCompanyId = entrepriseStore.currentItemId

                // Effectuer l'impersonation
                this.mainToken = await Api.admin.impersonatePersonnel(
                    personnel.IDPERSONNEL,
                )

                // Mettre à jour l'entreprise courante
                entrepriseStore.currentItemId = personnel.IDCOMPANY

                // Attendre que tous les stores soient prêts
                await this._waitForStoresReady()

                Logger.info(
                    "auth.store",
                    "Impersonation completed successfully",
                )
            } catch (e) {
                // En cas d'erreur, restaurer l'ancien token
                Logger.error("auth.store", "Failed to impersonate:", e)
                this.mainToken = oldToken
                throw e
            } finally {
                // Masquer le loader
                appStore.isChangingCompany = false
                appStore.changingCompanyMessage = null
            }
        },
        async impersonateClient(client: ClientResource) {
            const appStore = useAppStore()
            const entrepriseStore = useEntrepriseStore()
            const oldToken = this.mainToken

            try {
                Logger.info(
                    "auth.store",
                    `Starting impersonation of client ${client.IDCLIENT}`,
                    {
                        fromCompany: this.mainToken.company_id,
                        toCompany: client.IDCOMPANY,
                    },
                )

                // Afficher le loader
                appStore.isChangingCompany = true
                appStore.changingCompanyMessage = `Connexion en tant que ${client.NOM_COMPLET}...`

                // Sauvegarder le token actuel et l'entreprise consultée
                this.tempToken = this.mainToken
                this.impersonateReturnCompanyId = entrepriseStore.currentItemId

                // Effectuer l'impersonation
                this.mainToken = await Api.admin.adminImpersonateClient(
                    client.IDCLIENT,
                )

                // Mettre à jour l'entreprise courante
                entrepriseStore.currentItemId = client.IDCOMPANY

                // Attendre que tous les stores soient prêts
                await this._waitForStoresReady()

                Logger.info(
                    "auth.store",
                    "Client impersonation completed successfully",
                )
            } catch (e) {
                // En cas d'erreur, restaurer l'ancien token
                Logger.error("auth.store", "Failed to impersonate client:", e)
                this.mainToken = oldToken
                throw e
            } finally {
                // Masquer le loader
                appStore.isChangingCompany = false
                appStore.changingCompanyMessage = null
            }
        },
        async leaveImpersonate() {
            const appStore = useAppStore()
            const entrepriseStore = useEntrepriseStore()

            try {
                Logger.info("auth.store", "Leaving impersonation", {
                    fromCompany: this.mainToken.company_id,
                    toCompany: this.tempToken.company_id,
                })

                // Afficher le loader
                appStore.isChangingCompany = true
                appStore.changingCompanyMessage = "Retour à votre compte..."

                // Révoquer le token du personnel/client avant de restaurer le token admin
                try {
                    await Api.auth.logout()
                    Logger.info(
                        "auth.store",
                        "Impersonated token revoked successfully",
                    )
                } catch (e) {
                    Logger.warn(
                        "auth.store",
                        "Failed to revoke impersonated token:",
                        e,
                    )
                    // On continue même si la révocation échoue
                }

                // Restaurer le token principal
                this.mainToken = this.tempToken
                this.tempToken = {
                    token: "",
                    tokenable_type: "",
                    tokenable_id: "",
                    company_id: 0,
                }

                // Mettre à jour l'entreprise courante
                entrepriseStore.currentItemId = this.mainToken.company_id

                // Attendre que tous les stores soient prêts
                await this._waitForStoresReady()

                Logger.info("auth.store", "Left impersonation successfully")

                // Rediriger vers l'entreprise consultée avant l'usurpation
                const returnCompanyId = this.impersonateReturnCompanyId
                this.impersonateReturnCompanyId = null

                if (returnCompanyId) {
                    await router.push({
                        name: "admin.company.show",
                        params: { id: returnCompanyId, section: "personnels" },
                    })
                } else {
                    await router.push({ name: DOMAIN.admin.home })
                }
            } catch (e) {
                Logger.error("auth.store", "Failed to leave impersonate:", e)
                throw e
            } finally {
                // Masquer le loader
                appStore.isChangingCompany = false
                appStore.changingCompanyMessage = null
            }
        },
    },
})
